import React, { useState, useMemo } from 'react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell,
  AreaChart, Area, ReferenceLine
} from 'recharts';

const COLORS = ['#1976d2', '#e94560', '#4caf50', '#ff9800', '#9c27b0', '#00bcd4', '#795548', '#607d8b'];

function ResultsTable({ results, defaultViewMode, hideViewToggle, onViewModeChange, onChartClick, showRowIndex, showSearch, seriesColors, onSeriesDetected, unit }) {
  const [viewMode, setViewMode] = useState(defaultViewMode || 'table');
  const [searchText, setSearchText] = useState('');

  const changeViewMode = (mode) => {
    setViewMode(mode);
    if (onViewModeChange) onViewModeChange(mode);
  };

  const { chartData, numericColumns, groupColumn, labelColumn, pivotedGrouped, groupedSeries, pivotedStacked, stackedSeries } = useMemo(() => {
    if (!results?.results?.length || !results?.columns?.length) {
      return { chartData: [], numericColumns: [], groupColumn: null, pivotedGrouped: [], groupedSeries: [], pivotedStacked: [], stackedSeries: [] };
    }

    const rows = results.results;
    const columns = results.columns;

    // Find numeric columns (for Y-axis) - exclude label/name columns
    const numericCols = columns.filter(col => {
      const colLower = col.toLowerCase();
      // Exclude columns that are likely labels (but not metric names like "time_to_review")
      if (colLower.includes('label') || colLower === 'week' ||
          colLower === 'day' || colLower === 'date' ||
          colLower === 'timestamp' || colLower === 'time') {
        return false;
      }
      // Also exclude if it ends with _name or _label
      if (colLower.endsWith('_name') || colLower.endsWith('_label')) {
        return false;
      }
      const firstValue = rows.find(r => r[col] !== null && r[col] !== undefined)?.[col];
      return typeof firstValue === 'number';
    });

    // Find label column (for X-axis) - prefer label columns, then date/time columns, then string columns
    const labelNamedCol = columns.find(col =>
      col.toLowerCase().includes('label') ||
      col.toLowerCase().includes('week') ||
      col.toLowerCase().includes('name')
    );
    const dateCol = columns.find(col =>
      col.toLowerCase().includes('time') ||
      col.toLowerCase().includes('date') ||
      col.toLowerCase().includes('day')
    );
    const stringCol = columns.find(col => {
      const firstValue = rows.find(r => r[col] !== null)?.[col];
      return typeof firstValue === 'string';
    });
    const labelCol = labelNamedCol || dateCol || stringCol || columns[0];

    // Detect grouping column - a second string column that isn't the label
    const groupCol = columns.find(col => {
      if (col === labelCol) return false;
      if (numericCols.includes(col)) return false;
      const firstValue = rows.find(r => r[col] !== null)?.[col];
      return typeof firstValue === 'string';
    });

    // Transform data for chart
    const data = rows.map((row, idx) => {
      const item = { name: formatLabel(row[labelCol], labelCol) || `Row ${idx + 1}` };
      item._rawName = row[labelCol];
      numericCols.forEach(col => {
        item[col] = row[col];
      });
      return item;
    });

    // Pivot data for grouped/stacked charts when groupCol exists
    let pivotedGrouped = [];
    let groupedSeries = [];
    let pivotedStacked = [];
    let stackedSeries = [];
    if (groupCol && numericCols.length >= 1) {
      const valueCol = numericCols[0];

      // Grouped: labelCol on X-axis, one bar per groupCol value
      groupedSeries = [...new Set(rows.map(r => r[groupCol]).filter(v => v != null))];
      const groupedMap = {};
      rows.forEach(row => {
        const label = formatLabel(row[labelCol], labelCol) || 'Unknown';
        if (!groupedMap[label]) {
          groupedMap[label] = { name: label, _rawName: row[labelCol] };
          groupedSeries.forEach(g => { groupedMap[label][g] = 0; });
        }
        groupedMap[label][row[groupCol]] = row[valueCol] || 0;
      });
      pivotedGrouped = Object.values(groupedMap);

      // Stacked: groupCol on X-axis, one stacked segment per labelCol value
      stackedSeries = [...new Set(rows.map(r => formatLabel(r[labelCol], labelCol) || 'Unknown'))];
      const stackedMap = {};
      rows.forEach(row => {
        const xLabel = String(row[groupCol] || 'Unknown');
        const seriesKey = formatLabel(row[labelCol], labelCol) || 'Unknown';
        if (!stackedMap[xLabel]) {
          stackedMap[xLabel] = { name: xLabel, _rawName: row[groupCol] };
          stackedSeries.forEach(s => { stackedMap[xLabel][s] = 0; });
        }
        stackedMap[xLabel][seriesKey] = (stackedMap[xLabel][seriesKey] || 0) + (row[valueCol] || 0);
      });
      pivotedStacked = Object.values(stackedMap);
    }

    return { chartData: data, numericColumns: numericCols, groupColumn: groupCol, labelColumn: labelCol, pivotedGrouped, groupedSeries, pivotedStacked, stackedSeries };
  }, [results]);

  React.useEffect(() => {
    if (onSeriesDetected) {
      onSeriesDetected({ numericColumns, groupedSeries, stackedSeries });
    }
  }, [numericColumns, groupedSeries, stackedSeries, onSeriesDetected]);

  const filteredRows = useMemo(() => {
    const allRows = results?.results;
    const cols = results?.columns;
    if (!allRows || !cols) return [];
    if (!showSearch || !searchText.trim()) return allRows;
    const term = searchText.toLowerCase();
    return allRows.filter(row =>
      cols.some(col => {
        const val = row[col];
        return val !== null && val !== undefined && String(val).toLowerCase().includes(term);
      })
    );
  }, [results, searchText, showSearch]);

  if (!results || !results.results || results.results.length === 0) {
    return (
      <div className="results-table-container">
        <div className="results-header">
          <h3>Results</h3>
        </div>
        <p style={{ color: '#666', textAlign: 'center', padding: '2rem' }}>
          No results to display
        </p>
      </div>
    );
  }

  const { columns, row_count, execution_time_ms } = results;
  const rows = filteredRows;

  const formatValue = (value, col) => {
    if (value === null || value === undefined) {
      return <span style={{ color: '#666' }}>null</span>;
    }
    if (typeof value === 'string' && value.startsWith('http')) {
      return (
        <a href={value} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2' }}>
          {value}
        </a>
      );
    }
    if (typeof value === 'object') {
      return (
        <span className="json-cell" title={JSON.stringify(value, null, 2)}>
          {JSON.stringify(value).substring(0, 50)}
          {JSON.stringify(value).length > 50 ? '...' : ''}
        </span>
      );
    }
    if (typeof value === 'boolean') {
      return value ? 'true' : 'false';
    }
    if (typeof value === 'number') {
      if (value > 1000000) {
        return value.toLocaleString();
      }
      if (!Number.isInteger(value)) {
        return value.toFixed(2);
      }
    }
    return String(value);
  };

  function formatLabel(value, colName) {
    if (value === null || value === undefined) return '';

    // If column name contains 'label', use the value as-is (already formatted)
    if (colName?.toLowerCase().includes('label')) {
      return String(value);
    }

    // Format date columns
    if (colName?.toLowerCase().includes('time') ||
        colName?.toLowerCase().includes('date') ||
        colName?.toLowerCase() === 'day' ||
        colName?.toLowerCase() === 'week') {
      try {
        if (typeof value === 'string' && (value.includes('T') || /^\d{4}-\d{2}-\d{2}$/.test(value))) {
          const date = new Date(value + (value.includes('T') ? '' : 'T00:00:00'));
          if (!isNaN(date.getTime())) {
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          }
        }
      } catch {
        // Fall through to default
      }
    }
    if (typeof value === 'number' && colName?.toLowerCase().includes('id')) {
      return `ID: ${value}`;
    }
    return String(value).substring(0, 20);
  }

  const clickable = !!onChartClick;
  const cursorStyle = clickable ? { cursor: 'pointer' } : {};

  const handleElementClick = (data) => {
    if (!onChartClick) return;
    const payload = data?.payload || data;
    if (!payload) return;
    onChartClick({ labelColumn, labelValue: payload._rawName ?? payload.name });
  };

  const handlePieClick = (entry) => {
    if (!onChartClick) return;
    onChartClick({ labelColumn, labelValue: entry._rawName ?? entry.name });
  };

  const handleGroupedElementClick = (data, dataKey) => {
    if (!onChartClick) return;
    const payload = data?.payload || data;
    if (!payload) return;
    onChartClick({ labelColumn, labelValue: payload._rawName ?? payload.name, groupColumn, groupValue: dataKey });
  };

  const handleStackedElementClick = (data, dataKey) => {
    if (!onChartClick) return;
    const payload = data?.payload || data;
    if (!payload) return;
    onChartClick({ labelColumn: groupColumn, labelValue: payload._rawName ?? payload.name, groupColumn: labelColumn, groupValue: dataKey });
  };

  const renderChart = () => {
    if (numericColumns.length === 0) {
      return (
        <p style={{ color: '#666', textAlign: 'center', padding: '2rem' }}>
          No numeric columns found for charting
        </p>
      );
    }

    const chartHeight = hideViewToggle ? '100%' : 400;

    switch (viewMode) {
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={chartHeight}>
            <BarChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis
                dataKey="name"
                angle={-45}
                textAnchor="end"
                height={50}
                tick={{ fontSize: 11 }}
              />
              <YAxis tick={{ fontSize: 11 }} width={40} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: '0.75rem', paddingTop: 0 }} />
              {numericColumns.map((col, idx) => (
                <Bar key={col} dataKey={col} fill={seriesColors?.[col] || COLORS[idx % COLORS.length]} onClick={clickable ? handleElementClick : undefined} style={cursorStyle} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        );

      case 'line':
        return (
          <ResponsiveContainer width="100%" height={chartHeight}>
            <LineChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis
                dataKey="name"
                angle={-45}
                textAnchor="end"
                height={50}
                tick={{ fontSize: 11 }}
              />
              <YAxis tick={{ fontSize: 11 }} width={40} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: '0.75rem', paddingTop: 0 }} />
              {numericColumns.map((col, idx) => (
                <Line
                  key={col}
                  type="monotone"
                  dataKey={col}
                  stroke={seriesColors?.[col] || COLORS[idx % COLORS.length]}
                  strokeWidth={2}
                  dot={clickable ? { r: 5, style: { cursor: 'pointer' } } : { r: 4 }}
                  activeDot={clickable ? { r: 7, style: { cursor: 'pointer' }, onClick: (e, dotPayload) => handleElementClick(dotPayload) } : { r: 4 }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        );

      case 'pie':
        const pieData = chartData.slice(0, 10).map((item, idx) => ({
          name: item.name,
          _rawName: item._rawName,
          value: item[numericColumns[0]] || 0,
          fill: seriesColors?.[item.name] || COLORS[idx % COLORS.length]
        }));
        return (
          <ResponsiveContainer width="100%" height={chartHeight}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                labelLine={true}
                label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                outerRadius="70%"
                dataKey="value"
                onClick={handlePieClick}
                style={cursorStyle}
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} style={cursorStyle} />
                ))}
              </Pie>
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: '0.75rem', paddingTop: 0 }} />
            </PieChart>
          </ResponsiveContainer>
        );

      case 'grouped':
        return (
          <ResponsiveContainer width="100%" height={chartHeight}>
            <BarChart data={pivotedGrouped} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis
                dataKey="name"
                angle={-45}
                textAnchor="end"
                height={50}
                tick={{ fontSize: 11 }}
              />
              <YAxis tick={{ fontSize: 11 }} width={40} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: '0.75rem', paddingTop: 0 }} />
              {groupedSeries.map((group, idx) => (
                <Bar key={group} dataKey={group} fill={seriesColors?.[group] || COLORS[idx % COLORS.length]} onClick={clickable ? (data) => handleGroupedElementClick(data, group) : undefined} style={cursorStyle} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        );

      case 'stacked':
        return (
          <ResponsiveContainer width="100%" height={chartHeight}>
            <BarChart data={pivotedStacked} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis
                dataKey="name"
                angle={-45}
                textAnchor="end"
                height={50}
                tick={{ fontSize: 11 }}
              />
              <YAxis tick={{ fontSize: 11 }} width={40} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: '0.75rem', paddingTop: 0 }} />
              {stackedSeries.map((series, idx) => (
                <Bar key={series} dataKey={series} fill={seriesColors?.[series] || COLORS[idx % COLORS.length]} stackId="a" onClick={clickable ? (data) => handleStackedElementClick(data, series) : undefined} style={cursorStyle} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        );

      case 'metric': {
        const valueCol = numericColumns[0];
        const lastValue = chartData.length > 0 ? chartData[chartData.length - 1][valueCol] : 0;
        const values = chartData.map(d => d[valueCol]).filter(v => v != null);
        const minVal = Math.min(...values);
        const maxVal = Math.max(...values);
        const color = seriesColors?.[valueCol] || '#e8891c';
        const displayValue = Number.isInteger(lastValue) ? lastValue : (lastValue != null ? lastValue.toFixed(1) : '—');

        return (
          <div className="metric-widget-content">
            <div className="metric-sparkline-container">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 8, right: 0, left: 0, bottom: 8 }}>
                  <defs>
                    <linearGradient id={`metricGrad-${valueCol}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={color} stopOpacity={0.3} />
                      <stop offset="100%" stopColor={color} stopOpacity={0.05} />
                    </linearGradient>
                  </defs>
                  <YAxis domain={[minVal * 0.9, maxVal * 1.1]} hide />
                  <Area type="monotone" dataKey={valueCol} stroke={color} strokeWidth={1.5} fill={`url(#metricGrad-${valueCol})`} dot={false} isAnimationActive={false} />
                  {values.length > 1 && (
                    <>
                      <ReferenceLine y={maxVal} stroke={color} strokeDasharray="4 3" strokeOpacity={0.5} />
                      <ReferenceLine y={minVal} stroke="#4caf50" strokeDasharray="4 3" strokeOpacity={0.5} />
                    </>
                  )}
                </AreaChart>
              </ResponsiveContainer>
              <div className="metric-ref-label metric-ref-max" style={{ color }}>{Number.isInteger(maxVal) ? maxVal : maxVal.toFixed(1)}{unit ? ` ${unit}` : ''}</div>
              <div className="metric-ref-label metric-ref-min" style={{ color: '#4caf50' }}>{Number.isInteger(minVal) ? minVal : minVal.toFixed(1)}{unit ? ` ${unit}` : ''}</div>
            </div>
            <div className="metric-big-number" style={{ color }}>
              <span className="metric-value">{displayValue}</span>
              {unit && <span className="metric-unit">{unit}</span>}
            </div>
          </div>
        );
      }

      default:
        return null;
    }
  };

  if (viewMode === 'metric' && numericColumns.length > 0 && hideViewToggle) {
    return renderChart();
  }

  return (
    <div className="results-table-container" style={hideViewToggle ? { height: '100%' } : undefined}>
      {!hideViewToggle && (
        <div className="results-header">
          <h3>Results</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div className="view-toggle">
              <button
                className={viewMode === 'table' ? 'active' : ''}
                onClick={() => changeViewMode('table')}
              >
                Table
              </button>
              <button
                className={viewMode === 'bar' ? 'active' : ''}
                onClick={() => changeViewMode('bar')}
                disabled={numericColumns.length === 0}
              >
                Bar Chart
              </button>
              <button
                className={viewMode === 'grouped' ? 'active' : ''}
                onClick={() => changeViewMode('grouped')}
                disabled={!groupColumn}
              >
                Grouped
              </button>
              <button
                className={viewMode === 'stacked' ? 'active' : ''}
                onClick={() => changeViewMode('stacked')}
                disabled={!groupColumn}
              >
                Stacked
              </button>
              <button
                className={viewMode === 'line' ? 'active' : ''}
                onClick={() => changeViewMode('line')}
                disabled={numericColumns.length === 0}
              >
                Line Chart
              </button>
              <button
                className={viewMode === 'pie' ? 'active' : ''}
                onClick={() => changeViewMode('pie')}
                disabled={numericColumns.length === 0}
              >
                Pie Chart
              </button>
              <button
                className={viewMode === 'metric' ? 'active' : ''}
                onClick={() => changeViewMode('metric')}
                disabled={numericColumns.length === 0}
              >
                Metric
              </button>
            </div>
            <span className="results-stats">
              {row_count} rows | {execution_time_ms}ms
            </span>
          </div>
        </div>
      )}

      {viewMode === 'table' ? (
        <>
          {showSearch && (
            <div style={{ padding: '0.5rem 0' }}>
              <input
                type="text"
                placeholder="Search results..."
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.4rem 0.6rem',
                  fontSize: '0.85rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  boxSizing: 'border-box',
                }}
              />
            </div>
          )}
          <table className="results-table">
            <thead>
              <tr>
                {showRowIndex && <th style={{ width: '3rem', textAlign: 'center' }}>#</th>}
                {columns.map((col, i) => (
                  <th key={i}>{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {showRowIndex && <td style={{ textAlign: 'center', color: '#9ca3af' }}>{rowIndex + 1}</td>}
                  {columns.map((col, colIndex) => (
                    <td key={colIndex} title={String(row[col])}>
                      {formatValue(row[col], col)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>

          {row_count > rows.length && (
            <p style={{ color: '#666', textAlign: 'center', padding: '1rem' }}>
              Showing {rows.length} of {row_count} rows
            </p>
          )}
        </>
      ) : viewMode === 'metric' ? (
        <div style={hideViewToggle ? { height: '100%', position: 'relative' } : { height: '150px', position: 'relative', margin: '1rem 0' }}>
          {renderChart()}
        </div>
      ) : (
        <div style={hideViewToggle ? { height: '100%' } : { padding: '1rem 0' }}>
          {renderChart()}
        </div>
      )}
    </div>
  );
}

export default ResultsTable;
